/** @jsxImportSource @emotion/react */
import { LayerInfo } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/InfoAccordions/LayerInfo'
import { Box } from '@mui/material'
import { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { DataLayerDetailError } from '../components/DataLayerDetailError'
import DataLayerDetailHeader, {
  DataLayerDetailHeaderBackButton,
  DataLayerDetailHeaderTitle,
} from '../components/DataLayerDetailHeader'
import RiskDataDetailMap from '../components/DataLayerDetailMap'
import { DataLayerDetailPageSkeleton } from '../components/DataLayerDetailPageSkeleton'
import DataLayerDetailTabs from '../components/DataLayerDetailTabs'
import { useDataLayerScenario } from '../hooks/useDataLayerScenario'
import { useHazardLayerMetadata } from '../hooks/useHazardLayerMetadata'
import { useResolvedLayer } from '../hooks/useResolvedLayer'
import { useSelectedLayerDetail } from '../hooks/useSelectedLayerDetail'
import { content, mapPane, page, sideInner } from '../styles/DataLayerDetailPage.styles'
import { container } from '../styles/DataLayout.styles'

const DataLayerDetailPage = () => {
  const { layerId: id } = useParams<{ layerId: string }>()

  const layer = useResolvedLayer(id)
  const hazardMeta = useHazardLayerMetadata(id)
  const { selectedLayerDetail, isLoading, isError } = useSelectedLayerDetail(id)

  const [selectedAssetTag, setSelectedAssetTag] = useState<string | null>(id ?? null)

  const { activeDisplayName, activeTilesetIds } = useDataLayerScenario({
    layerId: id || '',
    selectedAssetTag,
    onSelectScenario: setSelectedAssetTag,
    fallbackDisplayName: selectedLayerDetail?.displayName,
    fallbackTilesetIds: selectedLayerDetail?.tilesetIds,
  })

  if (isLoading) return <DataLayerDetailPageSkeleton />
  if (isError || !id || !selectedLayerDetail) return <DataLayerDetailError />

  return (
    <Box data-testid="data-layer-detail-page" css={page}>
      <Box data-testid="data-layer-detail-page-container" css={container}>
        <Box data-testid="data-layer-detail-content" css={content}>
          <Box data-testid="data-layer-detail-map-pane" css={mapPane}>
            <RiskDataDetailMap
              tilesetIds={activeTilesetIds}
              element={layer?.type === 'Element' ? layer.element : null}
              elementId={id}
              contextual={layer?.type === 'Contextual' ? layer.contextual : null}
              contextualId={id}
            />
          </Box>

          <Box data-testid="data-layer-detail-side-panel" css={sideInner}>
            <Box data-testid="data-layer-detail-header">
              <DataLayerDetailHeader>
                <DataLayerDetailHeaderBackButton />
              </DataLayerDetailHeader>
              <DataLayerDetailHeaderTitle
                displayName={hazardMeta?.metadata?.title ?? activeDisplayName}
              />
            </Box>

            {layer?.type === 'Hazard' ? (
              <>
                <Box data-testid="data-layer-detail-tabs">
                  <DataLayerDetailTabs />
                </Box>
                <Box data-testid="data-layer-detail-outlet">
                  <Outlet
                    context={{
                      selectedLayerDetail,
                      layer,
                      hazardMeta,
                      selectedAssetTag,
                      onSelectScenario: setSelectedAssetTag,
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box data-testid="data-layer-detail-layer-info">
                <LayerInfo layerDetails={selectedLayerDetail.layerDetail} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default DataLayerDetailPage
